/**
 * WHAT:
 *   Custom hook for file handling operations (upload, drag & drop, parsing, scanning).
 *
 * WHY:
 *   Encapsulates all file-related operations for better organization.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Hooks
 *
 * DEPENDENCIES:
 *   - Tauri core API (invoke)
 *   - Tauri dialog plugin (open)
 *   - File utilities (readFileAsBase64, extractRawText, openPdfFile)
 *   - Validation utilities (validateParsedData)
 *
 * RISKS:
 *   - NOTE: Large PDF files may cause memory issues during Base64 conversion
 *   - WARN: File operations are async - ensure proper error handling
 *   - NOTE: Directory scanning may be slow with many files
 *   - NOTE: File paths may be invalid on different operating systems
 */

import type { PdfData, RawDataDebug, TestTabState } from "../types";

import { invoke } from "@tauri-apps/api/core";
import { useCallback } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";

import {
  readFileAsBase64,
  extractRawText,
  openPdfFile as openPdfFileUtility,
  validateParsedData,
} from "../utils";

/**
 * Extracts error message from unknown error type.
 *
 * @param error - The error to extract message from
 *
 *
 * @returns Error message string
 *
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error === undefined) {
    return "Unbekannter Fehler";
  }
  if (error === null) {
    return "Unbekannter Fehler";
  }
  return JSON.stringify(error);
}

/**
 * WHAT:
 *   Custom hook that provides file handling functions.
 *
 * WHY:
 *   Centralizes file operations for better organization.
 *
 * INPUTS:
 *   - state: TestTabState — State object containing all state values and setters
 *
 * OUTPUT:
 *   Object with all file handling functions.
 *
 * @param state - State object containing all state values and setters
 *
 *
 * @returns Object with all file handling functions
 *
 */
export const useFileHandlers = (state: TestTabState) => {
  const {
    allErrorPdfs,
    setAllErrorPdfs,
    setBrowseFailedOnly,
    setCurrentErrorIndex,
    setCurrentPdfIndex,
    setCurrentScanFile,
    setError,
    setFailedPdfs,
    setFileInputKey,
    setIsDragOver,
    setIsProcessing,
    setIsScanning,
    setParsedData,
    setParsedPdfs,
    setRawData,
    setRawPdfText,
    setScanProgress,
    setScanResults,
    setScanTotal,
    setShowRawData,
    setUploadedFile,
    setValidationStatus,
    setYahooLoading,
    setYahooResult,
    skipEnrichment,
    uploadedFile,
    yahooIdentifier,
  } = state;

  /**
   * WHAT:
   *   Resets all component state to initial values.
   *
   * WHY:
   *   Provides clean slate for new test operations.
   */
  const resetState = useCallback(() => {
    setUploadedFile(null);
    setParsedData(null);
    setRawPdfText("");
    setError(null);
    setAllErrorPdfs([]);
    setCurrentErrorIndex(0);
    // NOTE: Force file input re-render by incrementing key
    setFileInputKey((k) => k + 1);
    // NOTE: Reset PDF navigation state - clears parsed and failed PDF lists
    setParsedPdfs([]);
    setFailedPdfs([]);
    setCurrentPdfIndex(0);
    setScanResults(null);
    // NOTE: Reset Raw Data Debug state - clears validation and raw data display
    setRawData(null);
    setShowRawData(false);
    setValidationStatus({ canImport: false, errors: [], isValid: false });
  }, [
    setUploadedFile,
    setParsedData,
    setRawPdfText,
    setError,
    setAllErrorPdfs,
    setCurrentErrorIndex,
    setFileInputKey,
    setParsedPdfs,
    setFailedPdfs,
    setCurrentPdfIndex,
    setScanResults,
    setRawData,
    setShowRawData,
    setValidationStatus,
  ]);

  /**
   * WHAT:
   *   Handle file selection or drop.
   *
   * WHY:
   *   Validates and sets selected PDF file.
   */
  const handleFileSelect = useCallback(
    (file: File) => {
      if (file.type !== "application/pdf") {
        alert("Bitte nur PDF-Dateien auswählen.");
        return;
      }
      setUploadedFile(file);
      setParsedData(null);
      setRawPdfText("");
      setError(null);
      debugLog("info", "investments", "PDF selected", {
        fileName: file.name,
        module: "parqet",
      });
    },
    [setUploadedFile, setParsedData, setRawPdfText, setError],
  );

  /**
   * WHAT:
   *   Open PDF file in default application.
   *
   * WHY:
   *   Allows users to view PDF files in their default PDF viewer.
   */
  const openPdfFile = useCallback(
    async (filePath: string) => {
      try {
        await openPdfFileUtility(filePath);
      } catch (error: unknown) {
        const errorMessage = extractErrorMessage(error);
        setError(`Fehler beim Öffnen der PDF: ${errorMessage}`);
      }
    },
    [setError],
  );

  /**
   * WHAT:
   *   Load error PDF with text and data.
   *
   * WHY:
   *   Allows navigation through error PDFs.
   */
  const loadErrorPdf = useCallback(
    async (index: number) => {
      if (allErrorPdfs.length === 0) {
        return;
      }

      const errorPdf = allErrorPdfs[index];
      if (!errorPdf) {
        return;
      }
      setCurrentErrorIndex(index);

      // Set immediate placeholder
      setParsedData({
        error: errorPdf.errorMessage ?? "Parsing error",
        fileName: errorPdf.fileName,
        filePath: errorPdf.filePath,
      });
      setUploadedFile(new File([], errorPdf.fileName, { type: "application/pdf" }));

      try {
        const text = await invoke<string>("extract_pdf_text", {
          fileName: errorPdf.fileName,
          filePath: errorPdf.filePath,
          pdfData: "",
        });
        setRawPdfText(text || "⚠️ Kein Text extrahiert.");
      } catch (error: unknown) {
        const message = extractErrorMessage(error);
        debugLog("error", "investments", "PDF text extraction failed", {
          error: message,
          module: "parqet",
        });
        setRawPdfText(`❌ PDF-Text-Extraktion fehlgeschlagen: ${message}`);
      }
    },
    [allErrorPdfs, setCurrentErrorIndex, setParsedData, setUploadedFile, setRawPdfText],
  );

  const navigateToNextError = useCallback(async () => {
    if (allErrorPdfs.length === 0) {
      return;
    }
    const next = (state.currentErrorIndex + 1) % allErrorPdfs.length;
    await loadErrorPdf(next);
  }, [allErrorPdfs, state.currentErrorIndex, loadErrorPdf]);

  const navigateToPreviousError = useCallback(async () => {
    if (allErrorPdfs.length === 0) {
      return;
    }
    const previous =
      state.currentErrorIndex === 0 ? allErrorPdfs.length - 1 : state.currentErrorIndex - 1;
    await loadErrorPdf(previous);
  }, [allErrorPdfs, state.currentErrorIndex, loadErrorPdf]);

  /**
   * WHAT:
   *   Drag and drop handlers.
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(true);
    },
    [setIsDragOver],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    },
    [setIsDragOver],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      const file = files.length > 0 ? files[0] : undefined;
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect, setIsDragOver],
  );

  /**
   * WHAT:
   *   Parse PDF into structured data.
   *
   * WHY:
   *   Delegates PDF parsing to Rust backend.
   */
  const processPDF = useCallback(async () => {
    if (!uploadedFile) {
      return;
    }
    setIsProcessing(true);
    setError(null);

    try {
      const raw = await extractRawText(uploadedFile);
      setRawPdfText(raw);

      interface ParsedPdfResult {
        [key: string]: unknown;
        errorCount?: number;
        message?: string;
        success?: boolean;
        successCount?: number;
        totalPdfs?: number;
      }

      const base64 = await readFileAsBase64(uploadedFile);
      const parsed = await invoke<ParsedPdfResult>("parse_pdf_unified", {
        dryRun: true,
        fileName: uploadedFile.name,
        pdfData: base64,
        skipEnrichment: skipEnrichment,
      });
      setParsedData(parsed);
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      setError(message);
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedFile, skipEnrichment, setIsProcessing, setError, setRawPdfText, setParsedData]);

  /**
   * Handles successful PDF raw parsing.
   *
   * @param pdfText - Extracted PDF text
   *
   *
   * @param result - Parsed result
   *
   */
  const handleSuccessfulRawParsing = useCallback(
    (pdfText: string, result: Record<string, unknown>) => {
      setRawData(result);
      setShowRawData(true);

      // Validate the parsed data
      const validationResult = validateParsedData(result);
      setValidationStatus(validationResult);

      debugLog("info", "investments", "PDF raw parsing completed", {
        file_name: uploadedFile?.name ?? "unknown",
        module: "parqet",
        pdf_text_length: pdfText.length,
        result,
      });
    },
    [uploadedFile, setRawData, setShowRawData, setValidationStatus],
  );

  /**
   * Handles error during PDF raw parsing - tries to extract minimal debug info.
   *
   * @param message - Error message
   *
   */
  const handleRawParsingError = useCallback(
    async (message: string) => {
      if (!uploadedFile) {
        return;
      }

      setError(message);

      // Show debug window even on error - extract what we can
      try {
        const base64 = await readFileAsBase64(uploadedFile);
        const pdfText = await invoke<string>("extract_pdf_text", {
          fileName: uploadedFile.name,
          filePath: null,
          pdfData: base64,
        });
        setRawPdfText(pdfText);

        // Try to create a minimal debug object even if parsing failed
        const errorDebugData = {
          error: message,
          parsing_failed: true,
          pdf_text_length: pdfText.length,
          pdf_text_preview: pdfText.slice(0, 500),
          timestamp: new Date().toISOString(),
        };

        setRawData(errorDebugData);
        setShowRawData(true);
      } catch (innerError: unknown) {
        // If even debug extraction fails, show minimal info
        const innerErrorMessage = extractErrorMessage(innerError);
        const minimalDebugData = {
          debug_extraction_failed: true,
          error: message,
          // NOTE: propagate inner error information in a safe, stringified way
          inner_error: innerErrorMessage === "Unbekannter Fehler" ? null : innerErrorMessage,
          timestamp: new Date().toISOString(),
        };
        setRawData(minimalDebugData);
        setShowRawData(true);
      }

      debugLog("error", "investments", "PDF raw parsing failed", {
        error: message,
        file_name: uploadedFile.name,
        module: "parqet",
      });
    },
    [uploadedFile, setError, setRawPdfText, setRawData, setShowRawData],
  );

  /**
   * WHAT:
   *   Process PDF with raw data debug output.
   *
   * WHY:
   *   Provides comprehensive debugging information for PDF parsing.
   */
  const processPDFRaw = useCallback(async () => {
    if (!uploadedFile) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setRawData(null);
    setShowRawData(false);
    setRawPdfText(""); // Reset PDF text

    try {
      const base64 = await readFileAsBase64(uploadedFile);

      // First extract raw PDF text
      const pdfText = await invoke<string>("extract_pdf_text", {
        fileName: uploadedFile.name,
        filePath: null,
        pdfData: base64,
      });

      setRawPdfText(pdfText);

      interface ParseResult {
        [key: string]: unknown;
        isin?: string;
        transaction_type?: string;
      }

      // Parse PDF with validation (matches import logic)
      const result = await invoke<ParseResult>("parse_pdf_unified", {
        dryRun: true,
        fileName: uploadedFile.name,
        pdfData: base64,
        skipEnrichment: skipEnrichment,
      });

      handleSuccessfulRawParsing(pdfText, result);
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      void handleRawParsingError(message);
    } finally {
      setIsProcessing(false);
    }
  }, [
    uploadedFile,
    skipEnrichment,
    setIsProcessing,
    setError,
    setRawPdfText,
    handleSuccessfulRawParsing,
    handleRawParsingError,
  ]);

  /**
   * Handles early abort scenario during folder scan.
   *
   * @param failedPdf - The failed PDF that caused the abort
   *
   * @param total - Total number of PDFs
   *
   */
  const handleEarlyAbort = useCallback(
    (
      failedPdf: {
        file_name: string;
        error: string;
        raw_text?: string;
        parsed_data?: unknown;
      },
      total: number | undefined,
    ) => {
      debugLog("info", "investments", "Folder scan aborted due to first failure", {
        error: failedPdf.error,
        failedFile: failedPdf.file_name,
        module: "parqet",
        total,
      });

      // Set state for Single PDF Debug Mode (same as "Raw Data Debug" button)
      setRawPdfText(failedPdf.raw_text ?? "⚠️ No PDF text extracted");
      setError(failedPdf.error || "Unknown error");

      // Set uploaded file for UI consistency
      setUploadedFile(
        new File([], failedPdf.file_name, {
          type: "application/pdf",
        }),
      );

      // If parsed data exists (validation error), set it and validate
      if (failedPdf.parsed_data) {
        setRawData(failedPdf.parsed_data as RawDataDebug);
        setShowRawData(true);
        // Run validation to show validation errors
        const validationResult = validateParsedData(failedPdf.parsed_data);
        setValidationStatus(validationResult);
      } else {
        // Parsing error - no parsed data available
        setRawData(null);
        setShowRawData(true);
      }

      // Set scan results to show early abort info
      setScanResults({
        failed: 1,
        successful: 0,
        total: total ?? 0,
      });

      // Clear parsed PDFs since scan was aborted
      setParsedPdfs([]);
      setFailedPdfs([failedPdf as PdfData]);

      debugLog("info", "investments", "Early abort state set", {
        hasParsedData: !!failedPdf.parsed_data,
        hasRawText: !!failedPdf.raw_text,
        module: "parqet",
        showRawData: true,
      });
    },
    [
      setRawPdfText,
      setError,
      setUploadedFile,
      setRawData,
      setShowRawData,
      setValidationStatus,
      setScanResults,
      setParsedPdfs,
      setFailedPdfs,
    ],
  );

  /**
   * Loads the first failed PDF from scan results.
   *
   * @param first - First failed PDF
   *
   */
  const loadFirstFailedPdf = useCallback(
    (first: unknown) => {
      setBrowseFailedOnly(true);
      setCurrentPdfIndex(0);

      // Raw text setzen
      const typedFirst =
        first && typeof first === "object"
          ? (first as {
              raw_text?: unknown;
              error?: unknown;
              file_name?: unknown;
              parsed_data?: unknown;
              data?: unknown;
            })
          : {};
      const rawText =
        typedFirst.raw_text && typeof typedFirst.raw_text === "string"
          ? typedFirst.raw_text
          : "⚠️ No PDF text extracted";
      setRawPdfText(rawText);
      setShowRawData(true);
      const errorMessage =
        typedFirst.error && typeof typedFirst.error === "string" ? typedFirst.error : "";
      setError(errorMessage);
      const fileName =
        typedFirst.file_name && typeof typedFirst.file_name === "string"
          ? typedFirst.file_name
          : "unknown.pdf";
      setUploadedFile(
        new File([], fileName, {
          type: "application/pdf",
        }),
      );

      // Prüfe ob geparste Daten vorhanden (bei Validation Errors)
      if (typedFirst.parsed_data || typedFirst.data) {
        const parsedData = typedFirst.parsed_data ?? typedFirst.data;
        if (parsedData && typeof parsedData === "object") {
          setRawData(parsedData as Record<string, unknown>);
          setParsedData(parsedData as Record<string, unknown>);
          const validationResult = validateParsedData(parsedData as Record<string, unknown>);
          setValidationStatus(validationResult);
        }
      } else {
        setRawData(null);
        setParsedData(null);
      }
    },
    [
      setBrowseFailedOnly,
      setCurrentPdfIndex,
      setRawPdfText,
      setShowRawData,
      setError,
      setUploadedFile,
      setRawData,
      setParsedData,
      setValidationStatus,
    ],
  );

  /**
   * Loads the first successful PDF from scan results.
   *
   * @param first - First successful PDF
   *
   */
  const loadFirstSuccessfulPdf = useCallback(
    (first: unknown) => {
      setBrowseFailedOnly(false);
      setCurrentPdfIndex(0);

      // Parsed data setzen
      const typedFirst =
        first && typeof first === "object"
          ? (first as { data?: unknown; file_name?: unknown; raw_pdf_text?: unknown })
          : {};

      if (typedFirst.data && typeof typedFirst.data === "object") {
        const parsedData = typedFirst.data as Record<string, unknown>;
        setParsedData(parsedData);
        setRawData(parsedData);
        setShowRawData(true);
        const validationResult = validateParsedData(parsedData);
        setValidationStatus(validationResult);

        // File setzen für UI-Konsistenz
        const fileName =
          typedFirst.file_name && typeof typedFirst.file_name === "string"
            ? typedFirst.file_name
            : "unknown.pdf";
        setUploadedFile(
          new File([], fileName, {
            type: "application/pdf",
          }),
        );

        // Raw text falls vorhanden
        if (typedFirst.raw_pdf_text && typeof typedFirst.raw_pdf_text === "string") {
          setRawPdfText(typedFirst.raw_pdf_text);
        }
      }
    },
    [
      setBrowseFailedOnly,
      setCurrentPdfIndex,
      setParsedData,
      setRawData,
      setShowRawData,
      setValidationStatus,
      setUploadedFile,
      setRawPdfText,
    ],
  );

  /**
   * WHAT:
   *   Scan folder for PDFs.
   *
   * WHY:
   *   Allows batch processing of PDF files in a directory.
   */
  const handleScanFolder = useCallback(async () => {
    try {
      const { open } = await import("@tauri-apps/plugin-dialog");
      const selectedPath = await open({
        directory: true,
        title: "Wähle Ordner mit PDFs zum Scannen",
      });

      if (!selectedPath || Array.isArray(selectedPath)) {
        return;
      }

      setIsProcessing(true);
      setIsScanning(true);
      setScanProgress(0);
      setScanTotal(0);
      setCurrentScanFile("");
      setError(null);
      setParsedPdfs([]);
      setCurrentPdfIndex(0);
      setScanResults(null);

      debugLog("info", "investments", "Starting folder scan", {
        directory: selectedPath,
        module: "parqet",
        skipEnrichment,
      });

      interface ScanResult {
        early_abort?: boolean;
        failed?: number;
        failed_pdf?: {
          file_name: string;
          error: string;
          raw_text?: string;
          parsed_data?: unknown;
        };
        failed_pdfs?: unknown[];
        parsed_pdfs?: unknown[];
        processed?: number;
        successful?: number;
        total?: number;
      }

      // Call new backend function
      const result = await invoke<ScanResult>("scan_pdf_folder", {
        concurrencyOverride: 24,
        directoryPath: selectedPath,
        includeRawOnSuccess: false,
        skipEnrichment: skipEnrichment,
      });

      // 🔍 EARLY ABORT DETECTION: Check if scan was aborted due to first failure
      if (result.early_abort && result.failed_pdf) {
        handleEarlyAbort(result.failed_pdf, result.total);
        return; // Early return - don't continue with normal scan results
      }

      // ✅ NORMAL SCAN COMPLETION: No early abort
      setParsedPdfs((result.parsed_pdfs ?? []) as PdfData[]);
      setFailedPdfs((result.failed_pdfs ?? []) as PdfData[]);
      setScanResults({
        failed: result.failed ?? 0,
        successful: result.successful ?? 0,
        total: result.total ?? 0,
      });

      // Automatisch erste PDF laden (failed oder successful)
      const failures = result.failed_pdfs ?? [];
      const successes = result.parsed_pdfs ?? [];

      if (failures.length > 0) {
        // Fehlerhafte PDFs vorhanden → erste fehlerhafte laden
        loadFirstFailedPdf(failures[0]);
      } else if (successes.length > 0) {
        // Nur erfolgreiche PDFs → erste erfolgreiche laden
        loadFirstSuccessfulPdf(successes[0]);
      }

      debugLog("info", "investments", "Folder scan completed", {
        failed: result.failed,
        failedPdfsCount: result.failed_pdfs?.length ?? 0,
        module: "parqet",
        parsedPdfsCount: result.parsed_pdfs?.length ?? 0,
        successful: result.successful,
        total: result.total,
      });

      // Debug: Log the actual result structure
      debugLog("debug", "investments", "Scan result structure", {
        failed: result.failed,
        failedPdfsCount: result.failed_pdfs?.length ?? 0,
        module: "parqet",
        parsedPdfsCount: result.parsed_pdfs?.length ?? 0,
        successful: result.successful,
        total: result.total,
      });
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      setError(message);
      debugLog("error", "investments", "Folder scan failed", {
        error: message,
        module: "parqet",
      });
    } finally {
      setIsProcessing(false);
      setIsScanning(false);
    }
  }, [
    skipEnrichment,
    setIsProcessing,
    setIsScanning,
    setScanProgress,
    setScanTotal,
    setCurrentScanFile,
    setError,
    setParsedPdfs,
    setFailedPdfs,
    setCurrentPdfIndex,
    setScanResults,
    handleEarlyAbort,
    loadFirstFailedPdf,
    loadFirstSuccessfulPdf,
  ]);

  /**
   * WHAT:
   *   Test Yahoo Finance enrichment.
   *
   * WHY:
   *   Allows users to test Yahoo Finance API integration.
   */
  const testYahooEnrichment = useCallback(async () => {
    // NOTE: Validate ticker identifier is provided
    if (!yahooIdentifier.trim()) {
      setError("Bitte Ticker eingeben");
      return;
    }

    setYahooLoading(true);
    setYahooResult(null);
    setError(null);

    try {
      // NOTE: Call backend Yahoo Finance enrichment - ticker only, no ISIN fallback
      const result = await invoke("test_yahoo_enrichment", {
        identifier: yahooIdentifier.trim(),
        useIsin: false, // NOTE: Only ticker, no ISIN fallback
      });

      setYahooResult((result as null | { success: boolean }) ?? null);
      debugLog("info", "investments", "Yahoo enrichment test completed", {
        result,
      });
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      setError(`Yahoo Test fehlgeschlagen: ${errorMessage}`);
      setYahooResult({ error: errorMessage, success: false });
    } finally {
      setYahooLoading(false);
    }
  }, [yahooIdentifier, setYahooLoading, setYahooResult, setError]);

  return {
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileSelect,
    handleScanFolder,
    loadErrorPdf,
    navigateToNextError,
    navigateToPreviousError,
    openPdfFile,
    processPDF,
    processPDFRaw,
    resetState,
    testYahooEnrichment,
  };
};
